import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import request from 'lib/request';
import { toastAtom } from 'pages/type-curves/atoms';
import {
  unitAzimuthOverridesAtom,
  unitOverridesAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { azimuthOverrideKey } from 'pages/type-curves/azimuth-override';
import type {
  SlotRegeneration,
  UnitActionCreateResponse,
  UnitActionUndoResponse,
  UnitOverride,
  UnitOverridesListResponse,
} from 'types/api-responses';
import { describeRegenerationScope, unitRegenerationAtom } from './atoms';

/**
 * `GET /inventory_workspaces/:id/unit_actions` returns the same envelope and row
 * shape as the Type Curve workspace's unit actions, so the map layer's own types
 * are reused rather than redeclared.
 */
type InventoryUnitAction = UnitOverride;
type UnitActionsResponse = UnitOverridesListResponse;

/**
 * Rails renders rejections in two shapes, and this endpoint uses both: a model
 * validation comes back keyed by attribute (`{ errors: { base: [...] } }`), a
 * controller guard as a list (`{ errors: [{ message }] }`).
 */
type ActionFailure = {
  errors?: Record<string, string[]> | { message?: string }[];
};

/**
 * Pull the server's own wording out of a failure body.
 *
 * Worth the branching: the reasons a rotate is refused are ones only the server
 * knows and the operator can act on — "Workspace has been promoted and cannot be
 * modified" above all. A generic "Failed to rotate the unit" hides that and reads
 * as a bug in the button.
 *
 * @param {unknown} body - Parsed failure body, if the request got that far.
 * @return {string | null} First message, or null when the shape is unrecognised.
 */
const failureMessage = (body: unknown): string | null => {
  const errors = (body as ActionFailure | undefined)?.errors;
  if (!errors) return null;

  if (Array.isArray(errors)) {
    const message = errors.find((entry) => entry?.message)?.message;
    return message ?? null;
  }

  // Keyed by attribute: `base` first, since that is where a whole-record refusal
  // lands, then any other attribute that carries a message.
  const keys = ['base', ...Object.keys(errors).filter((k) => k !== 'base')];
  for (const key of keys) {
    const first = errors[key]?.find?.((entry) => Boolean(entry));
    if (first) return first;
  }
  return null;
};

export interface InventoryUnitRotation {
  /** The scoped unit carries a rotate action. */
  isRotated: boolean;
  /** A rotate or un-rotate is in flight. */
  pending: boolean;
  /** No unit scoped, so nothing to rotate. */
  disabled: boolean;
  /**
   * The bearing the unit's LTDs are at now: the one the server last stated this
   * session (after a rotate, un-rotate or restore), else the tile's baked one.
   * What the next rotate is measured from, and what the setback ring follows.
   */
  effectiveAzimuthDeg: number | null;
  toggle: () => Promise<void>;
}

/**
 * Rotate / un-rotate the builder's scoped unit.
 *
 * Separate from `useUnitRotation` in `components/type-curve-workspace`, which
 * posts to `/type_curve_workspaces/:id/header/unit_actions` and derives its
 * workspace from `activeTcWorkspaceAtom`. Neither applies here: this unit belongs
 * to an *inventory* workspace, which has its own route, and that atom is never
 * set on this page — so that hook returned `{ ok: false }` without issuing a
 * request, which is why Rotate appeared to do nothing.
 *
 * The backend stores the resulting absolute bearing (not a delta) and allows one
 * rotate per unit until it is undone, so un-rotate is a DELETE of that action.
 *
 * Every rotate response — create, undo, and the restore the server substitutes
 * when a request lands on the unit's detected axis — states the bearing the unit
 * is now headed to. That statement is written to `unitAzimuthOverridesAtom` and
 * is the only thing the map layers draw from; each map turns its own tile-built
 * frame onto it. Nothing here guesses what any tile currently bakes, which is
 * what the previous "rotated / un-rotated this session" bookkeeping did, and why
 * the builder and the main page could show the unit 90° apart.
 *
 * @param {number} inventoryWorkspaceId - Workspace the actions hang off.
 * @param {number | null} unitId - The scoped unit, or null for a draft.
 * @param {number | null} baseAzimuthDeg - The bearing this map's tile bakes for the unit.
 * @param {Function} onRotated - Called after a successful toggle with the
 *   regeneration the server started (or null). Azimuth defines the section line
 *   every formation is slotted against, so the current layout is invalid at the
 *   new bearing: the server now re-lays EVERY formation package asynchronously
 *   and says so in the response; a caller only needs its own re-slot when no
 *   regeneration came back.
 * @return {InventoryUnitRotation} Rotation state and the toggle.
 */
export function useInventoryUnitRotation(
  inventoryWorkspaceId: number,
  unitId: number | null,
  baseAzimuthDeg: number | null,
  onRotated?: (regeneration: SlotRegeneration | null) => void
): InventoryUnitRotation {
  const setToast = useSetAtom(toastAtom);
  const setUnitRegeneration = useSetAtom(unitRegenerationAtom);
  const setUnitOverrides = useSetAtom(unitOverridesAtom);
  const azimuthOverrides = useAtomValue(unitAzimuthOverridesAtom);
  const setAzimuthOverrides = useSetAtom(unitAzimuthOverridesAtom);
  const [actions, setActions] = useState<InventoryUnitAction[]>([]);
  const [pending, setPending] = useState(false);

  const basePath = `/inventory_workspaces/${inventoryWorkspaceId}/unit_actions`;

  const overrideKey =
    unitId === null ? null : azimuthOverrideKey(inventoryWorkspaceId, unitId);
  const stated =
    overrideKey === null ? undefined : azimuthOverrides.get(overrideKey);
  const effectiveAzimuthDeg = stated ?? baseAzimuthDeg;

  const load = useCallback(async () => {
    // `get` resolves to the payload itself (or `{ error }`), unlike `post`.
    const res = await request.get<UnitActionsResponse>(
      basePath,
      new AbortController()
    );
    if (!('unitActions' in res)) return;
    const rows = res.unitActions ?? [];
    setActions(rows);
    // The action list is what the Rotate / Restore label and the main page's
    // removed-unit overlay read. Nothing else writes it on this page, so the
    // workspace's actions ARE its unit overrides here.
    setUnitOverrides(rows);
  }, [basePath, setUnitOverrides]);

  useEffect(() => {
    void load();
  }, [load]);

  const rotateAction = actions.find(
    (a) => a.action === 'rotate' && a.unitId === unitId
  );

  const toggle = useCallback(async () => {
    if (unitId === null || pending || overrideKey === null) return;
    setPending(true);

    // Measured from where the LTDs are NOW, which is the server's last statement
    // when there is one. Measuring from the tile instead is what sent a unit
    // already restored to detected a request for detected + 180 — the same axis
    // — so the server restored it again instead of rotating it.
    const fromDeg = effectiveAzimuthDeg ?? 0;

    const controller = new AbortController();
    const res = rotateAction
      ? await request.deleteReq<UnitActionUndoResponse, ActionFailure>(
          `${basePath}/${rotateAction.id}`,
          null,
          controller
        )
      : await request.post<UnitActionCreateResponse, ActionFailure>(
          basePath,
          {
            unitAction: {
              unitId,
              action: 'rotate',
              // The API takes the final bearing, not a delta.
              azimuthRotationAngleDeg: (fromDeg + 90) % 360,
            },
          },
          controller
        );

    setPending(false);

    if (res.type !== 'success') {
      const reason =
        res.type === 'error'
          ? failureMessage(res.data)
          : // 'fatal' / 'abort' carry a transport message instead of a body.
            res.message;
      setToast({
        kind: 'error',
        title: rotateAction
          ? 'Failed to restore the unit azimuth'
          : 'Failed to rotate the unit',
        ...(reason ? { subtitle: reason } : {}),
      });
      return;
    }

    const body = res.data as
      UnitActionCreateResponse | UnitActionUndoResponse | null;
    // A rotate that landed back on the unit's detected axis writes no action: the
    // server re-laid the unit at its natural direction instead, so `load()` below
    // finds no rotate row and the button reads "Rotate" again — by design.
    const restoredDetected = body !== null && 'restoredDetectedAzimuth' in body;

    // The server's statement of where the unit is headed is the whole of the
    // client's rotation state. null means the unit has no derivable bearing, in
    // which case there is nothing to turn the frame onto and the entry is dropped.
    const resulting = body?.resultingAzimuthDeg;
    if (resulting !== undefined) {
      setAzimuthOverrides((prev) => {
        const next = new Map(prev);
        if (typeof resulting === 'number') next.set(overrideKey, resulting);
        else next.delete(overrideKey);
        return next;
      });
    }

    await load();

    // The server re-lays every formation package the unit belongs to, asynchronously, and says so
    // here. Hand it to the shared tally so the banner (modal + main page) shows it at once and the
    // progress hook can follow the unit channel; absent (nothing eligible / promoted) means no
    // regeneration was started.
    const regeneration = body?.slotRegeneration ?? null;
    if (regeneration) {
      setUnitRegeneration({
        unitId: regeneration.unitId,
        unitName: regeneration.unitName,
        message: regeneration.message,
        channel: regeneration.notifications.channel,
        totalFormations: regeneration.totalFormations,
        formations: regeneration.formations,
        // Wire array -> lookup map (the wire is an array because key camelization
        // would mangle formation-name keys).
        formationZoneCounts: Object.fromEntries(
          (regeneration.formationZoneCounts ?? []).map((entry) => [
            entry.formation,
            entry.zones,
          ])
        ),
        // Fall back to the formation count against a not-yet-redeployed server,
        // where each formation ran exactly one zone.
        totalZones: regeneration.totalZones ?? regeneration.totalFormations,
        zonesReported: {},
        totalZonesReported: 0,
        completed: 0,
        failedFormations: [],
        failed: 0,
        failedZones: 0,
        done: false,
      });
    }

    setToast({
      kind: 'success',
      title:
        rotateAction || restoredDetected ? 'Azimuth restored' : 'Rotated 90°',
      subtitle: regeneration
        ? // "Y inventories for X formations" (product wording, Misha) — same phrase as the banner.
          `Re-slotting ${describeRegenerationScope(
            regeneration.totalZones ?? regeneration.totalFormations,
            regeneration.totalFormations
          )} in the background.`
        : undefined,
    });
    onRotated?.(regeneration);
  }, [
    unitId,
    pending,
    overrideKey,
    effectiveAzimuthDeg,
    rotateAction,
    basePath,
    load,
    setToast,
    setUnitRegeneration,
    setAzimuthOverrides,
    onRotated,
  ]);

  return {
    isRotated: Boolean(rotateAction),
    pending,
    disabled: unitId === null,
    effectiveAzimuthDeg,
    toggle,
  };
}
